import { Directive } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import {
    ApiFragment,
    ApiQuery,
    Combine,
    defaultSortInfo,
    DottedKey,
    FragmentId,
    RecordPhase,
    RecordRestrictions,
    Restriction,
    sessionStorageHelper,
    ShToastFactory,
    singleResultPaginationInfo,
    StructuredQuery,
    User,
    Zone,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';
import { RecordsService } from '../common/services/api/records.service';
import { UserStoreService } from '../common/services/user/user-store.service';

@Directive()
export abstract class BaseSelectionService {
    protected mediaHavenUser$: Observable<User>;

    protected constructor(
        protected recordsService: RecordsService,
        protected userStoreService: UserStoreService,
        protected zoneRestrictions: ZoneRestrictions,
        protected zoneService: ZonesService,
        protected toastFactory: ShToastFactory,
        protected translateService: TranslateService
    ) {
        this.initUser();
    }

    protected initUser(): void {
        this.mediaHavenUser$ = this.userStoreService.getCurrentUser();
    }

    public getQuery(
        structuredQuery: StructuredQuery,
        selection: ApiFragment,
        fieldSelect?: DottedKey[]
    ): Observable<ApiQuery> {
        return this.getRestriction(selection).pipe(
            take(1),
            map(restrictions => {
                return new ApiQuery(structuredQuery, restrictions, fieldSelect);
            })
        );
    }

    private getZoneBasedRestriction(zones: Zone[]): Restriction {
        return sessionStorageHelper.has('shareToken')
            ? RecordRestrictions.conceptRestrictionShare()
            : RecordRestrictions.conceptZoneRestriction(zones);
    }

    public getRestriction(selection: ApiFragment): Observable<Restriction> {
        return this.zoneService.zones$.pipe(
            map(zones => {
                const statusRestrictions: Restriction[] = [
                    RecordRestrictions.phaseRestriction([RecordPhase.Published])
                ];

                if (sessionStorageHelper.has('shareToken')) {
                    statusRestrictions.push(RecordRestrictions.conceptRestrictionShare());
                } else if (zones && zones.length > 0) {
                    statusRestrictions.push(this.getZoneBasedRestriction(zones));
                }

                return Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    Combine.Or(...statusRestrictions),
                    RecordRestrictions.containedByRestriction(selection.getFragmentId())
                );
            })
        );
    }

    public getNrOfExportableChildren(fragmentId: FragmentId): Observable<number> {
        return this.getAllExportableSelectionFragments(fragmentId).pipe(
            map(exportableRecords => exportableRecords.length)
        );
    }

    public getNrOfNonExportableChildren(fragmentId: FragmentId): Observable<number> {
        return this.getAllSelectionFragments(fragmentId).pipe(
            map(records => records.filter(record => !this.isExportable(record)).length)
        );
    }

    public getNrOfConceptSelectionFragments(fragmentId: FragmentId): Observable<number> {
        return this.zoneService.zones$.pipe(
            take(1),
            map(zones =>
                Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.conceptZoneRestriction(zones),
                    RecordRestrictions.containedByRestriction(fragmentId)
                )
            ),
            switchMap(restriction =>
                this.recordsService.getRecords(
                    singleResultPaginationInfo,
                    defaultSortInfo,
                    new ApiQuery([], restriction)
                )
            ),
            map(paginatedResult => paginatedResult.paginationInfo.totalNumberOfResults)
        );
    }

    private getAllSelectionFragments(fragmentId: FragmentId): Observable<ApiFragment[]> {
        const restriction: Restriction = this.getContainedByRestrictions(fragmentId);

        return this.recordsService.getAllRecords(
            new ApiQuery([], restriction, [
                'Context' as DottedKey,
                'Internal.FragmentId' as DottedKey,
                'RightsManagement.RecordPhase' as DottedKey
            ]),
            {
                annotate: false
            }
        );
    }

    public getAllExportableSelectionFragments(fragmentId: FragmentId): Observable<ApiFragment[]> {
        return this.getAllSelectionFragments(fragmentId).pipe(
            map(records => records.filter(record => this.isExportable(record)))
        );
    }

    // #region Helpers
    private getContainedByRestrictions(fragmentId: FragmentId): Restriction {
        return Combine.And(
            this.zoneRestrictions.regularRecordTypesZone(),
            RecordRestrictions.containedByRestriction(fragmentId)
        );
    }

    private isExportable(record: ApiFragment): boolean {
        return record.hasExportRights() && record.getRecordPhase() !== RecordPhase.Concept;
    }
    // #endregion
}
